import api from "./api";
import { openExternal } from "./externalLink";

/**
 * Fire-and-forget partner click tracking. Does NOT block the anchor navigation —
 * the `<a href target="_blank">` handles the tab-opening natively (which avoids
 * popup blockers and the "about:blank" bug caused by window.open + noopener).
 *
 * Call it from the anchor's onClick handler WITHOUT calling e.preventDefault().
 * The href passed to the anchor already contains the UTM-tagged URL from
 * `/api/regions/:slug/spaces`, so the browser navigates to the tracked
 * destination on its own.
 *
 * @param {object|string} opts
 * @param {string} opts.slug             workspace slug
 * @param {string} [opts.name]           workspace display name (for return-feedback prompt)
 * @param {string} [opts.city]           workspace city
 * @param {string} [opts.source]         originating source token ('nh' | 'ma' | 'spaces' | ...)
 * @param {string} [opts.fallbackUrl]    fallback URL (kept for API compatibility, unused)
 * @param {string} [opts.bookingType]    external_direct_booking | external_inquiry | metier_checkout
 * @param {string} [opts.pageSource]     location pathname ('/nh', '/ma', ...)
 */
export function trackPartnerClick(opts) {
  // Support legacy positional-arg call signature: (slug, source, fallbackUrl, name)
  if (typeof opts === "string") {
    const [slug, source, fallbackUrl, name] = arguments;
    opts = { slug, source, fallbackUrl, name };
  }
  const {
    slug,
    name = null,
    city = null,
    source = "unknown",
    bookingType = null,
    pageSource,
  } = opts || {};

  // Stash a pending-return marker so the region page can prompt "Did you book?"
  // when the user comes back to Métier within a reasonable window.
  try {
    sessionStorage.setItem("metier_pending_click_feedback", JSON.stringify({
      slug,
      name,
      city,
      source,
      bookingType,
      at: Date.now(),
    }));
  } catch { /* private mode / sessionStorage disabled — ignore */ }

  // Fire-and-forget analytics POST. Errors are silently swallowed so a broken
  // backend never blocks a member from reaching a partner listing.
  api.post("/track/partner-click", {
    workspace_slug: slug,
    source: source || "unknown",
    booking_type: bookingType || null,
    destination_url: (opts && opts.fallbackUrl) || null,
    page_source: pageSource || (typeof window !== "undefined" ? window.location.pathname : null),
  }).catch(() => { /* ignore */ });

  // When running inside the Capacitor iOS shell, open the partner listing in
  // an SFSafariViewController instead of an OS app-switch. In the web build
  // this is a no-op because the anchor's target=_blank already handled it.
  if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
    const url = (opts && opts.fallbackUrl) || null;
    if (url) openExternal(url).catch(() => { /* ignore */ });
  }
}

export default trackPartnerClick;
